import envConfig from '@hulu/env-config';
import { bem } from 'mission-control-common-components';
import React, { useMemo, useState } from 'react';

import type { Assets2SubclassTypes } from '../../../../../apis/graphql';
import type { AssetType } from '../../../../../configs';
import { assetTypes } from '../../../../../configs';
import type { Asset2Extended } from '../../../../../models';
import AssetPreview from '../../../../AssetPreview';
import type { AssetPreviewProps } from '../../../../AssetPreview/AssetPreview';
import { NO_DISPLAY_ASSETS_TEXT, NO_TEXT_ASSETS_TEXT, NO_VIDEO_ASSETS_TEXT } from '../constants';

export type DxcCreativeTabItem = {
  key: Assets2SubclassTypes;
  title: string;
  element: React.JSX.Element;
  emptyMessage: string;
  tabIndex: number;
  itemProps: AssetPreviewProps[];
  previewElement: (props: AssetPreviewProps) => React.JSX.Element;
};

type UseDxcCreativeTabs = {
  selectedTab: Assets2SubclassTypes;
  handleTabClick: (type: Assets2SubclassTypes) => void;
  tabs: DxcCreativeTabItem[];
};

type UseDxcCreativeTabsProps = {
  displayAssets: Asset2Extended[];
  videoAssets: Asset2Extended[];
  textAssets: Asset2Extended[];
  lineItemPublisherList: string[];
};

const [, element] = bem('dxc-creative-builder');

export const useDxcCreativeTabs = ({
  displayAssets,
  videoAssets,
  textAssets,
  lineItemPublisherList,
}: UseDxcCreativeTabsProps): UseDxcCreativeTabs => {
  const [selectedTab, setSelectedTab] = useState<AssetType>(assetTypes.VideoAsset.key);

  const isTextAssetEnabled = envConfig.REACT_APP_FEATURE_ENABLE_TEXT_ASSET === 'true';

  const tabs = useMemo((): DxcCreativeTabItem[] => {
    return [
      {
        key: assetTypes.VideoAsset.key,
        title: assetTypes.VideoAsset.key,
        element: (
          <span className={element('tab-title')}>
            {`${assetTypes.VideoAsset.displayName}`}
            <span className={element('tab-counter')}>{` (${videoAssets.length})`}</span>
          </span>
        ),
        emptyMessage: NO_VIDEO_ASSETS_TEXT,
        tabIndex: 0,
        itemProps: videoAssets.map(
          (asset): AssetPreviewProps => ({
            asset,
            lineItemPublisherList,
          })
        ),
        previewElement: AssetPreview,
      },
      {
        key: assetTypes.DisplayAsset.key,
        title: assetTypes.DisplayAsset.key,
        element: (
          <span className={element('tab-title')}>
            {`${assetTypes.DisplayAsset.displayName}`}
            <span className={element('tab-counter')}>{` (${displayAssets.length})`}</span>
          </span>
        ),
        emptyMessage: NO_DISPLAY_ASSETS_TEXT,
        tabIndex: 1,
        itemProps: displayAssets.map(
          (asset): AssetPreviewProps => ({
            asset,
            lineItemPublisherList,
          })
        ),
        previewElement: AssetPreview,
      },
      ...(isTextAssetEnabled
        ? [
            {
              key: assetTypes.TextAsset.key,
              title: assetTypes.TextAsset.key,
              element: (
                <span className={element('tab-title')}>
                  {`${assetTypes.TextAsset.displayName}`}
                  <span className={element('tab-counter')}>{` (${textAssets.length})`}</span>
                </span>
              ),
              emptyMessage: NO_TEXT_ASSETS_TEXT,
              tabIndex: 2,
              itemProps: textAssets.map(
                (asset): AssetPreviewProps => ({
                  asset,
                  lineItemPublisherList,
                })
              ),
              previewElement: AssetPreview,
            },
          ]
        : []),
    ];
  }, [videoAssets, displayAssets, isTextAssetEnabled, textAssets, lineItemPublisherList]);

  const handleTabClick = (type: AssetType): void => {
    setSelectedTab(type);
  };

  return {
    selectedTab,
    handleTabClick,
    tabs,
  };
};
